import { useCallback } from "react";
import PageHeader from "../components/PageHeader";
import DateRangeFilter from "../components/DateRangeFilter";
import { ErrorBanner, LoadingNote, NoteCard } from "../components/Layout";
import { useDashboardData } from "../hooks/useDashboardData";
import { useDateRange } from "../hooks/useDateRange";
import {
  fetchEngagementStats,
  fetchOverview,
  fetchPlatformStats,
} from "../dashboardApi";
import {
  EngagementStatsResponse,
  OverviewResponse,
  PlatformStatsResponse,
} from "../types";
import AcquisitionSection from "./home/AcquisitionSection";
import MonetizationSection from "./home/MonetizationSection";
import RetentionSection from "./home/RetentionSection";
import ChannelSection from "./home/ChannelSection";
import HealthSection from "./home/HealthSection";

/**
 * Platform health, ordered by what gets looked at first rather than by where a
 * metric sits in the funnel.
 *
 * **The order changed in Sep 2026 and the reason is worth keeping.** This page
 * used to run acquisition → activation → monetization → retention → channel →
 * health, which is the shape of a funnel diagram and not the shape of anyone's
 * attention: texting is the product, and revenue is the question, so both sat
 * below three sections of leading indicators and a scroll. Jonathan's call was
 * "texting metrics first, then monetization, then acquisition and the rest of
 * the stuff at the bottom" — so the two sections that get read every day are the
 * two that open the page.
 *
 * **Activation is gone entirely, and it was not deleted so much as relocated.**
 * That section was 100% Instagram: a funnel chart plus three Instagram stat
 * tiles. Instagram now lives on its own screen in full — the funnel chart moved
 * there, where the per-creator table and the four timeline charts behind those
 * same steps already were — so Home shows no Instagram content at all rather
 * than a summary that had to be kept in step with a second copy. The one card in
 * that section that was NOT Instagram, the permanently-placeholdered Mixpanel
 * Standard user funnel, moved to Retention beside the other two session-metric
 * placeholders it belongs with.
 *
 * Cards are one of exactly two things, and they look different at a glance:
 * live (real Firestore aggregate, white card, real chart) or a labeled
 * placeholder (muted title, "Placeholder" chip, empty ghost frame, the exact
 * future source named). Nothing on this page shows an invented number.
 */
const HomeScreen = () => {
  const [range, setRange] = useDateRange();

  const fetcher = useCallback(() => fetchOverview(range), [range]);
  const { data, error, loading, refreshing } = useDashboardData<OverviewResponse>(
    fetcher,
    [range]
  );

  // The nightly rollup's daily rows, fetched alongside the live overview rather
  // than inside it: they come from different collections on different clocks,
  // and one failing should not blank the other.
  const platformFetcher = useCallback(() => fetchPlatformStats(range), [range]);
  const platform = useDashboardData<PlatformStatsResponse>(platformFetcher, [range]);

  // Signups and event attendance, from a different nightly document than the
  // platform rows above and fetched independently for the same reason: one
  // failing must not blank the other. (Sections owning an endpoint nothing else
  // on this page reads fetch it themselves — ChannelSection its Twilio call,
  // MonetizationSection the recurring-revenue history — so a failure there
  // degrades one card rather than the tab.)
  const engagementFetcher = useCallback(() => fetchEngagementStats(range), [range]);
  const engagement = useDashboardData<EngagementStatsResponse>(engagementFetcher, [
    range,
  ]);

  return (
    <div>
      <PageHeader
        title="Home"
        subtitle="Texting and revenue first, then the rest of the platform. The date range scopes the rollup and Twilio cards; the live Firestore counts are point-in-time, and the two full-history charts carry their own granularity toggle instead."
        right={<DateRangeFilter value={range} onChange={setRange} />}
      />

      {error && <ErrorBanner message={error} />}
      {loading && <LoadingNote />}

      {data && (
        <>
          {data.monetization.scanTruncated && (
            <div style={{ marginBottom: 20 }}>
              <NoteCard title="Customer scan truncated">
                This page aggregates the first {data.monetization.scanLimit} subscribed
                accounts. That ceiling has now been reached, so the monetization
                numbers below under-report — the endpoint needs real pagination
                before they can be trusted again.
              </NoteCard>
            </div>
          )}

          {/*
            Channel first, then Monetization: texting is the product and revenue
            is the question, and both used to sit below three sections a reader
            had to scroll past. Acquisition and the two placeholder-heavy
            sections keep their relative order beneath them.
          */}
          <ChannelSection
            range={range}
            platform={platform.data}
            platformError={platform.error}
            dimmed={platform.refreshing}
          />
          <MonetizationSection
            overview={data}
            platform={platform.data}
            platformError={platform.error}
            dimmed={refreshing}
          />
          <AcquisitionSection
            engagement={engagement.data}
            engagementError={engagement.error}
            dimmed={engagement.refreshing}
          />
          <RetentionSection />
          <HealthSection
            engagement={engagement.data}
            engagementError={engagement.error}
            dimmed={engagement.refreshing}
          />
        </>
      )}
    </div>
  );
};

export default HomeScreen;
